import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  List,
  Pagination,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  MailOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import useEmails from "hooks/emails/useEmails";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text } = Typography;
const PAGE_SIZE = 20;

const displayDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const listValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => item?.email || item?.address || item?.name || item)
      .filter(Boolean)
      .join(", ");
  }
  return String(value || "").trim();
};

const EmailMessages = () => {
  const { loading, error, getSentEmails } = useEmails();
  const [messages, setMessages] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);

  const sentCount = messages.length;
  const deliveredCount = useMemo(
    () =>
      messages.filter((item) =>
        String(item?.status || item?.emailStatus || "").toUpperCase().includes("DELIVER"),
      ).length,
    [messages],
  );

  const loadMessages = useCallback(async (nextPage = page) => {
    try {
      setLoadError("");
      const result = await getSentEmails(nextPage - 1, PAGE_SIZE);
      setMessages(Array.isArray(result) ? result : []);
    } catch (err) {
      const nextError = err?.message || "Failed to load email messages.";
      setLoadError(nextError);
      message.error(nextError);
    }
  }, [getSentEmails, page]);

  useEffect(() => {
    loadMessages(page);
  }, [loadMessages, page]);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const paginationControl =
    page === 1 && messages.length < PAGE_SIZE ? null : (
      <Pagination
        current={page}
        pageSize={PAGE_SIZE}
        total={page * PAGE_SIZE + (messages.length === PAGE_SIZE ? PAGE_SIZE : 0)}
        showSizeChanger={false}
        onChange={handlePageChange}
      />
    );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<MailOutlined />}
          title="Email Messages"
          subtitle="Track Olreadia email communication and sent message activity"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => loadMessages(page)}
          loading={loading}
          style={{
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            color: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreenSoft,
            fontWeight: 700,
          }}
        >
          Refresh
        </Button>
      </div>

      {loadError || error ? (
        <Alert
          type="warning"
          showIcon
          message="Email messages could not fully load"
          description={loadError || error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Space wrap size={12} style={{ marginBottom: 16 }}>
        <Card
          size="small"
          style={{
            minWidth: 180,
            borderRadius: 8,
            borderColor: OLIVEEB_COLORS.oliveebBlue,
            background: OLIVEEB_COLORS.oliveebBlueSoft,
          }}
        >
          <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900 }}>
            Sent messages
          </Text>
          <div style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontSize: 24, fontWeight: 900 }}>
            {sentCount}
          </div>
        </Card>
        <Card
          size="small"
          style={{
            minWidth: 180,
            borderRadius: 8,
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreenSoft,
          }}
        >
          <Text style={{ color: OLIVEEB_COLORS.oliveebGreen, fontWeight: 900 }}>
            Delivered
          </Text>
          <div style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 24, fontWeight: 900 }}>
            {deliveredCount}
          </div>
        </Card>
      </Space>

      <Card
        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Sent email messages</Text>}
        style={{
          borderRadius: 8,
          borderColor: OLIVEEB_COLORS.border,
        }}
      >
        {paginationControl ? (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            {paginationControl}
          </div>
        ) : null}

        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : messages.length ? (
          <List
            dataSource={messages}
            renderItem={(item) => (
              <List.Item
                style={{
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                  border: `1px solid ${OLIVEEB_COLORS.border}`,
                  background: OLIVEEB_COLORS.white,
                }}
              >
                <List.Item.Meta
                  avatar={<SendOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 18 }} />}
                  title={
                    <Space wrap size={[8, 4]}>
                      <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                        {item.subject || item.title || "Untitled message"}
                      </Text>
                      {item.status || item.emailStatus ? (
                        <Tag color="blue">{item.status || item.emailStatus}</Tag>
                      ) : null}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                      <Text type="secondary">
                        To: {listValue(item.to || item.recipients) || "-"}
                      </Text>
                      <Text type="secondary">
                        Sent: {displayDate(item.sentAt || item.createdAt || item.updatedAt)}
                      </Text>
                      {item.body ? (
                        <Paragraph
                          ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                          style={{ marginBottom: 0, color: OLIVEEB_COLORS.textMuted }}
                        >
                          {item.body}
                        </Paragraph>
                      ) : null}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No email messages found" />
        )}

        {paginationControl ? (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            {paginationControl}
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default EmailMessages;
